import { tierFullName, Translate } from "../ChoosePlanCarePlus/planContent";

/**
 * HL-11359 — rows for the "Kostenaufstellung" cost breakdown (Figma 5200:114443).
 *
 * Every row carries both what the line would cost on its own (`listPrice`) and what the customer
 * is actually charged for it here (`chargedPrice`). The component strikes the list price only
 * when the charged price is lower, so a waived line renders "29,00 € 0 €" and a normally charged
 * line renders a single value. Nothing here decides *why* a line is waived — that is already
 * encoded in the totals the cart returns.
 */
export interface CarePlusCostBreakdownRow {
  key: string;
  title: string;
  description: string;
  listPrice: number;
  chargedPrice: number;
  isDiscount?: boolean;
}

interface CostBreakdownRowsInput {
  t: Translate;
  /** Plan fee; > 0 renders one "Care+ Plan" row (plan + delivery) instead of the itemised fees. */
  planFee?: number;
  planTier?: string;
  planListPrice?: number;
  planChargedPrice?: number;
  /** Medication price as shown in the cart's medication line. */
  medicationTotal: number;
  /** Treatment fee list price (`cart.treatment_fee`), before the plan covers it. */
  treatmentListPrice: number;
  /** Treatment fee actually charged on top of the plan — 0 when the plan covers it. */
  treatmentChargedPrice: number;
  /** Service fee list price (`cart.service_fee`), before any plan/bonus coverage. */
  serviceFeeListPrice: number;
  /** Service fee actually charged on top of the plan — 0 when the plan covers it. */
  serviceFeeChargedPrice: number;
  /** Delivery list price, before any free-delivery promotion. */
  deliveryListPrice: number;
  /** Delivery actually charged. */
  deliveryChargedPrice: number;
  /** Discount not already shown as a struck-through fee row (voucher, recurring coupon). */
  discount?: number;
  /** Recurring (injection) coupon — adds the "applied every month" note. */
  isRecurringDiscount?: boolean;
}

export const getCarePlusCostBreakdownRows = ({
  t,
  planFee = 0,
  planTier = "",
  planListPrice = 0,
  planChargedPrice = 0,
  medicationTotal,
  treatmentListPrice,
  treatmentChargedPrice,
  serviceFeeListPrice,
  serviceFeeChargedPrice,
  deliveryListPrice,
  deliveryChargedPrice,
  discount = 0,
  isRecurringDiscount = false,
}: CostBreakdownRowsInput): CarePlusCostBreakdownRow[] => [
  {
    key: "medication",
    title: t("breakdown_medication", "Medication"),
    description: t("breakdown_medication_desc", "EU-approved and from registered pharmacies"),
    listPrice: medicationTotal,
    chargedPrice: medicationTotal,
  },
  // Plan order: the plan row mirrors the cart card ("Care+ Plan", plan fee + delivery) instead of
  // itemising treatment / service / delivery.
  ...(planFee > 0 ? [{
    key: "plan",
    // HL-12122: the V3 Monthly plan (tier "monthly") is not a Care+ tier.
    title: planTier === "monthly" ? t("plan_row_monthly", "Monthly Plan") : `${tierFullName(planTier, t)} ${t("plan_suffix", "Plan")}`,
    description: `${t("cadence_monthly", "Pay every month")} (${t("delivery_included", "Delivery included")})`,
    listPrice: planListPrice,
    chargedPrice: planChargedPrice,
  }] : [
  ...(treatmentListPrice > 0 ? [{
    key: "treatment",
    title: t("breakdown_treatment", "Treatment costs"),
    description: t("breakdown_treatment_desc", "Licensed prescribers"),
    listPrice: treatmentListPrice,
    chargedPrice: treatmentChargedPrice,
  }] : []),
  ...(serviceFeeListPrice > 0 ? [{
    key: "service",
    title: t("breakdown_service", "Processing & handling"),
    description: t(
      "breakdown_service_desc",
      "Licensed processing of German prescriptions, pharmacy coordination, storage & shipment tracking",
    ),
    listPrice: serviceFeeListPrice,
    chargedPrice: serviceFeeChargedPrice,
  }] : []),
  {
    key: "delivery",
    title: t("breakdown_delivery", "Delivery"),
    description: t("breakdown_delivery_desc", "Discreet, trackable, in 1-2 working days"),
    listPrice: deliveryListPrice,
    chargedPrice: deliveryChargedPrice,
  }]),
  // Discount stays last, same as the next-monthly-payment card: cost lines first, then what
  // comes off the total.
  ...(discount > 0 ? [{
    key: "discount",
    title: t("discount", "Discount"),
    description: isRecurringDiscount ? t("discount_recurring_desc", "Applied every month") : "",
    listPrice: discount,
    chargedPrice: discount,
    isDiscount: true,
  }] : []),
];
